import { SidebarNavigationContainer, SidebarNavigationItem } from "@king-ict/eupisi-ui/components";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";

import { RoleEnum } from "../../models/User/RoleEnum";
import { useGetUserSessionQuery } from "../../queries/userQuery";
import {
  codebookRouteProgramType, codebookRouteProgramName,
  codebookRouteLanguage, codebookRouteLanguageLevel,
  codebookRouteEducationSector, codebookRouteEducationSubSector,
  codebookRouteProfession, codebookRouteFormOfTeaching,
  codebookRouteEnrollmentCondition, codebookRouteKnowledgeCheck
} from "../../routes/routes";
import CodeBookEducationSector from "./CodeBookEducationSector";
import CodeBookEducationSectorDetail from "./CodeBookEducationSectorDetail";
import CodeBookEducationSectorEdit from "./CodeBookEducationSectorEdit";
import CodeBookEducationSubSector from "./CodeBookEducationSubSector";
import CodeBookEducationSubSectorDetail from "./CodeBookEducationSubSectorDetail";
import CodeBookEnrollmentCondition from "./CodeBookEnrollmentCondition";
import CodeBookEnrollmentConditionDetails from "./CodeBookEnrollmentConditionDetails";
import CodeBookEnrollmentConditionEdit from "./CodeBookEnrollmentConditionEdit";
import CodeBookFormOfTeaching from "./CodeBookFormOfTeaching";
import CodeBookFormOfTeachingDetails from "./CodeBookFormOfTeachingDetails";
import CodeBookFormOfTeachingEdit from "./CodeBookFormOfTeachingEdit";
import CodeBookKnowledgeCheck from "./CodeBookKnowledgeCheck";
import CodeBookKnowledgeCheckDetails from "./CodeBookKnowledgeCheckDetails";
import CodeBookKnowledgeCheckEdit from "./CodeBookKnowledgeCheckEdit";
import CodeBookLanguage from "./CodeBookLanguage";
import CodeBookLanguageDetail from "./CodeBookLanguageDetail";
import CodeBookLanguageEdit from "./CodeBookLanguageEdit";
import CodeBookLanguageLevel from "./CodeBookLanguageLevel";
import CodeBookLanguageLevelDetail from "./CodeBookLanguageLevelDetail";
import CodeBookLanguageLevelEdit from "./CodeBookLanguageLevelEdit";
import CodeBookProfession from "./CodeBookProfession";
import CodeBookProfessionDetails from "./CodeBookProfessionDetails";
import CodeBookProfessionEdit from "./CodeBookProfessionEdit";
import CodeBookProgramName from "./CodeBookProgramName";
import CodeBookProgramNameDetail from "./CodeBookProgramNameDetail";
import CodeBookProgramNameEdit from "./CodeBookProgramNameEdit";
import CodeBookProgramType from "./CodeBookProgramType";
import CodeBookProgramTypeDetail from "./CodeBookProgramTypeDetail";
import CodeBookProgramTypeEdit from "./CodeBookProgramTypeEdit";
import styles from "./RequestCodeBook.module.scss";

export function RequestCodeBook() {
  const { t } = useTranslation();

  const { data: userSession } = useGetUserSessionQuery();
  const location = useLocation();

  if (!userSession || userSession.roleId === RoleEnum.InstitutionAdmin || userSession.roleId === RoleEnum.InstitutionDirector) {
    return null;
  }

  return (
    <div className={styles.sidebarNavigationPanel}>
      <SidebarNavigationContainer
        color="primary"
        customTitle={t("codeBook.sidebarTitle")}
        key={`programType`}
      >
        <SidebarNavigationItem
          name={t("codeBook.programType")}
          route={codebookRouteProgramType()}
          selectedByRoute={true}
          key={`programType`}
        >
          {(location.state as any)?.page === 'details' &&
            <CodeBookProgramTypeDetail />
          }
          {(location.state as any)?.page === 'edit' &&
            <CodeBookProgramTypeEdit />
          }
          {(location.state as any)?.page !== 'details' && (location.state as any)?.page !== 'edit' &&
            <CodeBookProgramType />
          }
        </SidebarNavigationItem>
        <SidebarNavigationItem
          name={t("common.programName")}
          route={codebookRouteProgramName()}
          selectedByRoute={true}
          key={`programName`}
        >
          {(location.state as any)?.page === 'details' &&
            <CodeBookProgramNameDetail />
          }
          {(location.state as any)?.page === 'edit' &&
            <CodeBookProgramNameEdit />
          }
          {(location.state as any)?.page !== 'details' && (location.state as any)?.page !== 'edit' &&
            <CodeBookProgramName />
          }
        </SidebarNavigationItem>
        <SidebarNavigationItem
          name={t("codeBook.language")}
          route={codebookRouteLanguage()}
          selectedByRoute={true}
          key={`language`}
        >
          {(location.state as any)?.page === 'details' &&
            <CodeBookLanguageDetail />
          }
          {(location.state as any)?.page === 'edit' &&
            <CodeBookLanguageEdit />
          }
          {(location.state as any)?.page !== 'details' && (location.state as any)?.page !== 'edit' &&
            <CodeBookLanguage />
          }
        </SidebarNavigationItem>
        <SidebarNavigationItem
          name={t("codeBook.languageLevel")}
          route={codebookRouteLanguageLevel()}
          selectedByRoute={true}
          key={`languageLevel`}
        >
          {(location.state as any)?.page === 'details' &&
            <CodeBookLanguageLevelDetail />
          }
          {(location.state as any)?.page === 'edit' &&
            <CodeBookLanguageLevelEdit />
          }
          {(location.state as any)?.page !== 'details' && (location.state as any)?.page !== 'edit' &&
            <CodeBookLanguageLevel />
          }
        </SidebarNavigationItem>
        <SidebarNavigationItem
          name={t("codeBook.educationSector")}
          route={codebookRouteEducationSector()}
          selectedByRoute={true}
          key={`educationSector`}
        >
          {(location.state as any)?.page === 'details' &&
            <CodeBookEducationSectorDetail />
          }
          {
            (location.state as any)?.page === 'edit' &&
            <CodeBookEducationSectorEdit />
          }
          {(location.state as any)?.page !== 'details' && (location.state as any)?.page !== 'edit' &&
            <CodeBookEducationSector />
          }
        </SidebarNavigationItem>
        <SidebarNavigationItem
          name={t("codeBook.educationSubSector")}
          route={codebookRouteEducationSubSector()}
          selectedByRoute={true}
          key={`educationSubSector`}
        >
          {(location.state as any)?.page === 'details' &&
            <CodeBookEducationSubSectorDetail />
          }
          {(location.state as any)?.page !== 'details' && (location.state as any)?.page !== 'edit' &&
            <CodeBookEducationSubSector />
          }
        </SidebarNavigationItem>
        <SidebarNavigationItem
          name={t("codeBook.profession")}
          route={codebookRouteProfession()}
          selectedByRoute={true}
          key={`profession`}
        >
          {
            (location.state as any)?.page === 'details' &&
            <CodeBookProfessionDetails />
          }
          {
            (location.state as any)?.page === 'edit' &&
            <CodeBookProfessionEdit />
          }
          {
            (!(location.state as any)?.page || (location.state as any)?.page === '' || (location.state as any)?.page === 'list') &&
            <CodeBookProfession />
          }
        </SidebarNavigationItem>
        <SidebarNavigationItem
          name={t("codeBook.formOfTeaching")}
          route={codebookRouteFormOfTeaching()}
          selectedByRoute={true}
          key={`formOfTeaching`}
        >
          {
            (location.state as any)?.page === 'details' &&
            <CodeBookFormOfTeachingDetails />
          }
          {
            (location.state as any)?.page === 'edit' &&
            <CodeBookFormOfTeachingEdit />
          }
          {
            (!(location.state as any)?.page || (location.state as any)?.page === '' || (location.state as any)?.page === 'list') &&
            <CodeBookFormOfTeaching />
          }
        </SidebarNavigationItem>
        <SidebarNavigationItem
          name={t("codeBook.enrollmentCondition")}
          route={codebookRouteEnrollmentCondition()}
          selectedByRoute={true}
          key={`enrollmentCondition`}
        >
          {
            (location.state as any)?.page === 'details' &&
            <CodeBookEnrollmentConditionDetails />
          }
          {
            (location.state as any)?.page === 'edit' &&
            <CodeBookEnrollmentConditionEdit />
          }
          {
            (!(location.state as any)?.page || (location.state as any)?.page === '' || (location.state as any)?.page === 'list') &&
            <CodeBookEnrollmentCondition />
          }
        </SidebarNavigationItem>
        <SidebarNavigationItem
          name={t("codeBook.knowledgeCheck")}
          route={codebookRouteKnowledgeCheck()}
          selectedByRoute={true}
          key={`knowledgeChecks`}
        >
          {
            (location.state as any)?.page === 'details' &&
            <CodeBookKnowledgeCheckDetails />
          }
          {
            (location.state as any)?.page === 'edit' &&
            <CodeBookKnowledgeCheckEdit />
          }
          {
            (!(location.state as any)?.page || (location.state as any)?.page === '' || (location.state as any)?.page === 'list') &&
            <CodeBookKnowledgeCheck />
          }
        </SidebarNavigationItem>
      </SidebarNavigationContainer>
    </div>
  );
}

